import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Sparkles, Feather, ArrowRight, HeartHandshake, Compass,
    ShieldCheck, Users, Waves,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Seo from "@/components/Seo";
import { FeaturedVideoBlock } from "@/components/FeaturedVideoBlock";
import FAQ from "@/content/langageFaq.json";

const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

/**
 * Pilier SEO — /langage-de-lumiere
 *
 * Page profonde (~2500 mots) sur le mot-clé pilier de la Foundation of Light.
 * Structure : Origine · Manifestation · Bienfaits · Séance · Comparatif ·
 * Éthique · FAQ (avec schema FAQPage pour les résultats enrichis Google).
 */
const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQ.map((f) => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
};

const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Langage de lumière — canal vibratoire",
    "name": "Le langage de lumière — Foundation of Light",
    "provider": {
        "@type": "Organization",
        "name": "Foundation of Light",
        "url": "https://foundation-of-light.com",
    },
    "areaServed": "Worldwide",
    "description": "Le langage de lumière : canal vibratoire d'éveil de conscience, de soins énergétiques, de guidance et de libération. Transmissions individuelles et collectives à distance.",
    "offers": {
        "@type": "Offer",
        "url": "https://foundation-of-light.com/reservation",
        "priceCurrency": "EUR",
    },
};

function Section({ id, title, kicker, icon: Icon, children }) {
    return (
        <motion.section
            id={id}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeUp}
            className="max-w-3xl mx-auto px-6 py-16 md:py-20"
            data-testid={`section-${id}`}
        >
            <div className="flex items-center gap-3 mb-4">
                {Icon && <Icon className="w-5 h-5 text-primary" strokeWidth={1.6} aria-hidden />}
                <div className="text-xs tracking-[0.35em] uppercase text-primary font-medium">{kicker}</div>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl leading-tight mb-8">{title}</h2>
            <div className="space-y-5 text-lg text-foreground/85 leading-relaxed">{children}</div>
        </motion.section>
    );
}

export default function LangageDeLumiere() {
    return (
        <div data-testid="pilier-langage-de-lumiere">
            <Seo
                title="Le langage de lumière · Foundation of Light"
                description="Le langage de lumière : origine, bienfaits, déroulé d'une séance et comparatif avec les autres soins énergétiques, canalisé par Olivier Masurel."
                path="/langage-de-lumiere"
                jsonLd={[serviceJsonLd, faqJsonLd]}
            />

            {/* HERO */}
            <section className="relative overflow-hidden bg-gradient-to-b from-[#0b1424] via-[#111e35] to-background" data-testid="pilier-hero">
                <div className="absolute inset-0 pointer-events-none opacity-30">
                    <div className="absolute top-20 left-10 w-80 h-80 rounded-full bg-amber-200/40 blur-3xl" />
                    <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full bg-primary/30 blur-3xl" />
                </div>
                <div className="relative max-w-4xl mx-auto px-6 py-28 md:py-36 text-center">
                    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}>
                        <div className="text-xs tracking-[0.45em] uppercase text-amber-200/90 mb-6 font-medium">
                            Foundation of Light
                        </div>
                        <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl leading-[1.02] text-white mb-8">
                            Le langage<br />
                            <span className="italic text-amber-200">de lumière</span>
                        </h1>
                        <p className="text-xl text-white/80 leading-relaxed max-w-2xl mx-auto italic">
                            Un canal vibratoire d'éveil, de soin et de libération —
                            transmis à travers le corps, offert au vivant.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Sommaire / anchor nav */}
            <div className="bg-muted/40 border-y border-border/40">
                <div className="max-w-4xl mx-auto px-6 py-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground justify-center">
                    <a href="#origine" className="hover:text-primary transition-colors" data-testid="toc-origine">Origine</a>
                    <a href="#manifestation" className="hover:text-primary transition-colors" data-testid="toc-manifestation">Manifestation</a>
                    <a href="#bienfaits" className="hover:text-primary transition-colors" data-testid="toc-bienfaits">Bienfaits</a>
                    <a href="#seance" className="hover:text-primary transition-colors" data-testid="toc-seance">La séance</a>
                    <a href="#comparatif" className="hover:text-primary transition-colors" data-testid="toc-comparatif">Comparatif</a>
                    <a href="#ethique" className="hover:text-primary transition-colors" data-testid="toc-ethique">Éthique</a>
                    <a href="#faq" className="hover:text-primary transition-colors" data-testid="toc-faq">FAQ</a>
                </div>
            </div>

            {/* 1. ORIGINE */}
            <Section id="origine" kicker="Origine" title="D'où vient le langage de lumière ?" icon={Feather}>
                <p>
                    Le <strong>langage de lumière</strong> n'est pas une invention humaine. C'est un flux
                    vibratoire — sons, syllabes, gestes, symboles — qui se dépose à travers un canal ouvert,
                    sans passer par le mental. Il émerge d'une <em>intelligence plus vaste que l'individu</em>,
                    parfois appelée conscience unifiée, source, ou champ.
                </p>
                <p>
                    Au sein de la Foundation of Light, ce langage a émergé spontanément chez Olivier Masurel
                    à l'été 2024, à la suite de plusieurs années d'ouverture spirituelle et d'apprentissages
                    énergétiques. Il ne s'est ni cherché ni forcé — il s'est <em>reçu</em>. Découvrir{" "}
                    <Link to="/histoire" className="underline decoration-primary/40 hover:decoration-primary underline-offset-4">
                        le parcours qui l'a rendu possible
                    </Link>.
                </p>
                <p>
                    Contrairement à une langue humaine, le langage de lumière ne s'apprend pas dans un manuel.
                    Il se laisse traverser. Chaque canal qui le porte le manifeste avec sa propre couleur —
                    un peu comme un instrument unique jouant une même musique.
                </p>
            </Section>

            {/* 2. MANIFESTATION */}
            <Section id="manifestation" kicker="Manifestation" title="Comment se manifeste-t-il ?" icon={Waves}>
                <p>
                    Le langage de lumière s'exprime sous plusieurs formes, souvent conjuguées durant une même
                    transmission&nbsp;:
                </p>
                <ul className="space-y-3 pl-5 list-disc marker:text-primary/70">
                    <li><strong>Sons et syllabes</strong> — proches d'un chant sacré, sans grammaire ni signification littérale.</li>
                    <li><strong>Gestes</strong> — mouvements des mains, ancrages, dessins vibratoires dans l'espace.</li>
                    <li><strong>Symboles</strong> — parfois visibles à l'œil intérieur ou dessinés, ils portent une information codée.</li>
                    <li><strong>Silences habités</strong> — une présence dense, qui touche autant que les mots.</li>
                </ul>
                <p>
                    Ces manifestations agissent à un niveau que le mental ne peut pas décoder. Elles parlent
                    directement au corps, aux mémoires cellulaires, et aux couches subtiles de l'être.
                </p>
            </Section>

            {/* 3. BIENFAITS */}
            <Section id="bienfaits" kicker="Bienfaits" title="Ce que le langage de lumière peut ouvrir en vous" icon={Sparkles}>
                <p>
                    Les personnes qui reçoivent le langage de lumière rapportent des effets multiples,
                    variables selon leur ouverture et leur besoin du moment. Parmi les plus fréquents&nbsp;:
                </p>
                <ul className="space-y-3 pl-5 list-disc marker:text-primary/70">
                    <li><strong>Apaisement profond</strong> — sensation de retour au calme, comme après une longue méditation.</li>
                    <li><strong>Libération émotionnelle</strong> — larmes silencieuses, souffles longs, décharges de tensions anciennes.</li>
                    <li><strong>Réalignement</strong> — impression d'être « re-accordé » comme un instrument.</li>
                    <li><strong>Clarté intérieure</strong> — les décisions se posent d'elles-mêmes dans les jours qui suivent.</li>
                    <li><strong>Guidance</strong> — messages, images, intuitions qui répondent à une question portée en soi.</li>
                    <li><strong>Ouverture spirituelle</strong> — sentiment d'appartenance à quelque chose de plus vaste.</li>
                </ul>
                <p>
                    Le langage de lumière ne guérit pas — il <em>ouvre</em>. Il crée les conditions pour que
                    quelque chose bouge en vous, à votre rythme, dans le respect de vos rythmes intérieurs.
                </p>
            </Section>

            {/* 4. LA SÉANCE */}
            <Section id="seance" kicker="La séance" title="Recevoir un soin par le langage de lumière" icon={HeartHandshake}>
                <p>
                    Une séance individuelle dure environ <strong>60 minutes</strong> et se déroule à distance,
                    en visioconférence. Elle se compose de trois temps&nbsp;:
                </p>
                <ol className="space-y-3 pl-5 list-decimal marker:text-primary marker:font-semibold">
                    <li>
                        <strong>Accueil</strong> (10-15 min) — vous partagez ce qui vous amène, ce que vous
                        portez, ce que vous cherchez. Aucun jugement, aucune analyse. Simplement écouter.
                    </li>
                    <li>
                        <strong>Transmission</strong> (30-40 min) — vous vous allongez ou vous asseyez
                        confortablement. Le langage de lumière est transmis à travers la voix, les gestes et
                        l'intention. Vous n'avez rien à faire — juste recevoir.
                    </li>
                    <li>
                        <strong>Intégration</strong> (10 min) — quelques mots doux pour ancrer ce qui s'est
                        déposé, et vous laisser repartir dans votre journée avec ce qui vous appartient.
                    </li>
                </ol>
                <div className="pt-4">
                    <Link to="/reservation">
                        <Button size="lg" className="rounded-full" data-testid="cta-reservation">
                            Réserver une séance
                            <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </Link>
                </div>
            </Section>

            {/* 5. COMPARATIF */}
            <Section id="comparatif" kicker="Comparatif" title="Langage de lumière vs autres soins énergétiques" icon={Compass}>
                <p>
                    Le langage de lumière partage avec le Reiki, le magnétisme et les autres soins
                    énergétiques une même racine&nbsp;: <em>travailler avec l'énergie subtile</em>. Mais il s'en
                    distingue par plusieurs aspects&nbsp;:
                </p>
                <div className="overflow-x-auto -mx-6 md:mx-0">
                    <table className="w-full text-sm md:text-base border-collapse">
                        <thead>
                            <tr className="border-b-2 border-border/60 text-left">
                                <th className="py-3 pl-6 md:pl-0 pr-3 font-medium text-foreground">Aspect</th>
                                <th className="py-3 px-3 font-medium text-foreground">Reiki / Magnétisme</th>
                                <th className="py-3 pl-3 pr-6 md:pr-0 font-medium text-primary">Langage de lumière</th>
                            </tr>
                        </thead>
                        <tbody className="text-foreground/80">
                            <tr className="border-b border-border/40">
                                <td className="py-3 pl-6 md:pl-0 pr-3 font-medium">Nature</td>
                                <td className="py-3 px-3">Canalisation d'énergie</td>
                                <td className="py-3 pl-3 pr-6 md:pr-0">Canal vibratoire multi-forme (son, geste, symbole)</td>
                            </tr>
                            <tr className="border-b border-border/40">
                                <td className="py-3 pl-6 md:pl-0 pr-3 font-medium">Transmission</td>
                                <td className="py-3 px-3">Silencieuse, mains posées</td>
                                <td className="py-3 pl-3 pr-6 md:pr-0">Audible, mouvante, incarnée</td>
                            </tr>
                            <tr className="border-b border-border/40">
                                <td className="py-3 pl-6 md:pl-0 pr-3 font-medium">Apprentissage</td>
                                <td className="py-3 px-3">Structuré (niveaux, initiations)</td>
                                <td className="py-3 pl-3 pr-6 md:pr-0">Émergent, non-linéaire, singulier à chaque canal</td>
                            </tr>
                            <tr className="border-b border-border/40">
                                <td className="py-3 pl-6 md:pl-0 pr-3 font-medium">Cadre</td>
                                <td className="py-3 px-3">Souvent affilié à une lignée</td>
                                <td className="py-3 pl-3 pr-6 md:pr-0">Sans dogme, sans lignée, ouvert à tou·tes</td>
                            </tr>
                            <tr>
                                <td className="py-3 pl-6 md:pl-0 pr-3 font-medium">Ressenti</td>
                                <td className="py-3 px-3">Chaleur, apaisement</td>
                                <td className="py-3 pl-3 pr-6 md:pr-0">Vibrations profondes, larmes, réalignements, guidance</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <p className="pt-2 italic text-foreground/70">
                    Aucune approche n'est meilleure qu'une autre — chacune est une porte, et les portes
                    s'ouvrent différemment selon les êtres. Le langage de lumière parle à celles et ceux qui
                    ressentent l'appel du son et du vibratoire vivant.
                </p>
            </Section>

            {/* 6. ÉTHIQUE */}
            <Section id="ethique" kicker="Éthique" title="Cadre, éthique et limites" icon={ShieldCheck}>
                <p>
                    Le langage de lumière est un accompagnement <strong>énergétique et spirituel</strong>. Il
                    ne remplace en aucun cas&nbsp;:
                </p>
                <ul className="space-y-2 pl-5 list-disc marker:text-primary/70">
                    <li>Un diagnostic ou un traitement médical</li>
                    <li>Un suivi psychologique ou psychiatrique</li>
                    <li>Une intervention d'urgence</li>
                </ul>
                <p>
                    Il vient en <em>complément harmonieux</em> d'un parcours de soin. Nous n'induisons jamais
                    l'arrêt d'un traitement, et nous n'affirmons aucune promesse de guérison. Chaque personne
                    est libre, souveraine, et responsable de son propre chemin.
                </p>
                <p>
                    La Foundation of Light s'engage à un cadre bienveillant, respectueux du consentement,
                    et transparent sur ses tarifs et ses modalités.
                </p>
            </Section>

            {/* 7. FAQ */}
            <Section id="faq" kicker="Questions fréquentes" title="Vous vous demandez peut-être…" icon={Users}>
                <div className="space-y-6">
                    {FAQ.map((f, i) => (
                        <details key={i} className="group border-b border-border/40 pb-4" data-testid={`faq-${i}`}>
                            <summary className="cursor-pointer font-medium text-foreground text-lg flex items-start gap-3 list-none">
                                <span className="text-primary mt-1">·</span>
                                <span className="flex-1">{f.q}</span>
                            </summary>
                            <p className="mt-3 pl-6 text-foreground/75 text-base leading-relaxed">
                                {f.a}
                            </p>
                        </details>
                    ))}
                </div>
            </Section>

            {/* Vidéo — transmission mise en avant, pont vers /transmissions */}
            <FeaturedVideoBlock
                eyebrow="Vidéo · Transmission"
                title="Recevoir une transmission de langage de lumière"
                description="Une porte d'entrée vibratoire — laissez-vous traverser par le son, la présence et l'intention. Chaque transmission est un moment d'ouverture, à recevoir en cœur ouvert."
            />

            {/* CTA FINAL */}
            <section className="bg-primary/5 border-t border-primary/10" data-testid="pilier-cta-final">
                <div className="max-w-3xl mx-auto px-6 py-20 text-center">
                    <div className="text-xs tracking-[0.45em] uppercase text-primary mb-6 font-medium">Rejoindre</div>
                    <h2 className="font-serif text-3xl sm:text-4xl mb-6">
                        Si le langage de lumière vous appelle,<br />
                        <span className="italic">laissez-vous porter vers ce qui est juste pour vous.</span>
                    </h2>
                    <div className="flex flex-wrap gap-3 justify-center pt-4">
                        <Link to="/reservation">
                            <Button size="lg" className="rounded-full" data-testid="cta-final-reservation">
                                Recevoir un soin individuel
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </Link>
                        <Link to="/evenements">
                            <Button size="lg" variant="outline" className="rounded-full" data-testid="cta-final-events">
                                Rejoindre un atelier collectif
                            </Button>
                        </Link>
                        <Link to="/journal">
                            <Button size="lg" variant="ghost" className="rounded-full" data-testid="cta-final-journal">
                                Lire le journal
                            </Button>
                        </Link>
                    </div>
                    <p className="pt-8 text-sm text-muted-foreground italic">
                        Écrit et transmis par Olivier Masurel — canal du langage de lumière depuis 2024.{" "}
                        <Link to="/histoire" className="underline underline-offset-2 hover:text-primary">Découvrir son parcours</Link>.
                    </p>
                </div>
            </section>
        </div>
    );
}
